import { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, Clock, AlertTriangle } from 'lucide-react';
import { useForceRefresh, acknowledgeForceRefresh } from '@/hooks/useForceRefresh';
import { useAuth } from '@/context/AuthContext';
import { useUpdateAnimation } from '@/context/UpdateAnimationContext';

/**
 * Format seconds into a human-readable countdown string.
 */
function formatCountdown(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Non-dismissible modal that appears when an admin triggers a force refresh.
 *
 * This modal cannot be closed - the user must click "Refresh Now" to reload
 * the page and get the latest version.
 *
 * For scheduled refreshes, shows a countdown timer.
 * When autoRefresh is enabled, automatically reloads after the countdown.
 */
export function ForceRefreshModal() {
  const { authState } = useAuth();
  const {
    isRequired,
    reason,
    isScheduled,
    timeRemaining,
    source,
    autoRefresh,
    autoRefreshDelay,
    refreshId,
  } = useForceRefresh();

  const userId = authState.status === 'authenticated' ? authState.user.uid : undefined;
  const { triggerRefresh } = useUpdateAnimation();

  // Local countdown for immediate auto-refresh
  const [immediateCountdown, setImmediateCountdown] = useState<number | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasTriggeredAutoRefresh = useRef(false);
  // Extract complex expression to a variable for deps array
  const hasImmediateCountdown = immediateCountdown !== null;

  // Handle auto-refresh for immediate mode
  useEffect(() => {
    // Only start countdown for immediate (non-scheduled) with autoRefresh enabled
    if (isRequired && !isScheduled && autoRefresh && immediateCountdown === null) {
      setImmediateCountdown(autoRefreshDelay);
    }

    // Reset when not required
    if (!isRequired) {
      setImmediateCountdown(null);
      hasTriggeredAutoRefresh.current = false;
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [isRequired, isScheduled, autoRefresh, autoRefreshDelay, immediateCountdown]);

  // Countdown timer for immediate auto-refresh
  useEffect(() => {
    if (immediateCountdown === null || immediateCountdown <= 0) return;

    countdownIntervalRef.current = setInterval(() => {
      setImmediateCountdown((prev) => {
        if (prev === null) return null;
        return Math.max(0, prev - 1);
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImmediateCountdown]);

  // Define handleRefresh first so it can be used in effects
  const handleRefresh = useCallback(async () => {
    // Acknowledge the refresh to prevent showing again after reload
    if (source) {
      await acknowledgeForceRefresh(source, refreshId, userId);
    }
    // Trigger refresh with update animation
    triggerRefresh();
  }, [source, refreshId, userId, triggerRefresh]);

  // Auto-refresh when immediate countdown reaches 0
  useEffect(() => {
    if (immediateCountdown === 0 && autoRefresh && !hasTriggeredAutoRefresh.current) {
      hasTriggeredAutoRefresh.current = true;
      handleRefresh();
    }
  }, [immediateCountdown, autoRefresh, handleRefresh]);

  // Auto-refresh when scheduled time is reached
  useEffect(() => {
    if (
      isRequired &&
      !isScheduled &&
      autoRefresh &&
      timeRemaining !== null &&
      timeRemaining <= 0 &&
      !hasTriggeredAutoRefresh.current
    ) {
      hasTriggeredAutoRefresh.current = true;
      handleRefresh();
    }
  }, [isRequired, isScheduled, autoRefresh, timeRemaining, handleRefresh]);

  // Emergency escape hatch: add ?skip_force_refresh=true to URL to bypass
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('skip_force_refresh') === 'true') {
    return null;
  }

  // Don't show if not required
  if (!isRequired) {
    return null;
  }

  // If scheduled and time remaining, show countdown
  if (isScheduled && timeRemaining !== null && timeRemaining > 0) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fadeIn">
          {/* Header */}
          <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
            <Clock size={24} className="text-white" />
            <h2 className="text-xl font-bold text-white font-['Merriweather']">Update Scheduled</h2>
          </div>

          {/* Content */}
          <div className="p-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock size={40} className="text-amber-600" />
            </div>

            <p className="text-slate-700 mb-4">
              An important update has been scheduled.{' '}
              {autoRefresh
                ? 'Your browser will automatically refresh in:'
                : 'Your browser will need to refresh in:'}
            </p>

            <div className="text-4xl font-bold text-amber-600 mb-4 font-mono">
              {formatCountdown(timeRemaining)}
            </div>

            {reason && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Reason:</span> {reason}
                </p>
              </div>
            )}

            <p className="text-sm text-slate-500 mb-6">
              {autoRefresh
                ? 'The page will refresh automatically, or you can refresh now.'
                : 'You can refresh now to get the update immediately, or wait for the timer.'}
            </p>

            <button
              onClick={handleRefresh}
              className="w-full px-6 py-3 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw size={20} />
              Refresh Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Immediate force refresh (with optional auto-refresh countdown)
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="bg-[#8e4585] px-6 py-4 flex items-center gap-3">
          <AlertTriangle size={24} className="text-white" />
          <h2 className="text-xl font-bold text-white font-['Merriweather']">Update Required</h2>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <div className="w-20 h-20 mx-auto mb-4 bg-[#f0d2f0] rounded-full flex items-center justify-center">
            <RefreshCw
              size={40}
              className={`text-[#8e4585] ${autoRefresh && immediateCountdown !== null ? 'animate-spin' : ''}`}
            />
          </div>

          <p className="text-slate-700 mb-4">
            {autoRefresh && immediateCountdown !== null
              ? 'An important update is available. Your browser will automatically refresh.'
              : 'An important update is available. Please refresh your browser to continue using the app.'}
          </p>

          {/* Auto-refresh countdown */}
          {autoRefresh && immediateCountdown !== null && immediateCountdown > 0 && (
            <div className="mb-4">
              <p className="text-sm text-slate-500 mb-2">Auto-refreshing in:</p>
              <div className="text-3xl font-bold text-[#8e4585] font-mono">
                {formatCountdown(immediateCountdown)}
              </div>
            </div>
          )}

          {reason && (
            <div className="bg-[#fcf4fc] border border-[#e5bce5] rounded-lg p-3 mb-4">
              <p className="text-sm text-[#63345e]">
                <span className="font-medium">Reason:</span> {reason}
              </p>
            </div>
          )}

          <p className="text-sm text-slate-500 mb-6">
            This action was triggered by an administrator. Your current session will be preserved.
          </p>

          <button
            onClick={handleRefresh}
            className="w-full px-6 py-3 bg-[#8e4585] text-white rounded-xl font-bold hover:bg-[#753a6f] transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw size={20} />
            Refresh Now
          </button>
        </div>
      </div>

      {/* Fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
